/** Parser de texto colado do Excel (TSV) para linhas do Relatório. */

export type TipoColunaExcel =
  | 'peca'
  | 'qtd'
  | 'medida'
  | 'largura'
  | 'comprimento'
  | 'material'
  | 'espessura'
  | 'ignorar';

export interface LinhaParseadaExcel {
  nomePeca: string;
  quantidade: number;
  medidaMenor: number;
  medidaMaior: number;
  material: 'mdf' | 'aglomerado';
  espessura: number;
}

export interface ResultadoParseExcel {
  linhas: LinhaParseadaExcel[];
  /** Primeira linha do paste (cabeçalhos detectados ou brutos). */
  cabecalhosBrutos: string[];
  /** Mapeamento coluna índice → tipo. */
  mapaColunas: TipoColunaExcel[];
  avisos: string[];
}

function parseNum(s: string): number {
  const t = String(s ?? '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.');
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : 0;
}

function detectarTipoColuna(cel: string): TipoColunaExcel {
  const n = cel
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
  if (!n) return 'ignorar';
  if (/peca|descricao|nome|item/.test(n)) return 'peca';
  if (/qtd|quant|qtde/.test(n)) return 'qtd';
  if (/^medida$|dimensao|tamanho|mm/.test(n)) return 'medida';
  if (/larg|largura|menor|comp\.?\s*menor/.test(n)) return 'largura';
  if (/comp|compr|maior|alt/.test(n)) return 'comprimento';
  if (/mat|mdf|aglo/.test(n)) return 'material';
  if (/esp|mm/.test(n) && /esp/.test(n)) return 'espessura';
  return 'ignorar';
}

function parseMedidaCelula(cel: string): { menor: number; maior: number } | null {
  const t = String(cel || '').trim();
  if (!t) return null;
  const partes = t.split(/[x×*]/i).map((p) => parseNum(p)).filter((n) => n > 0);
  if (partes.length >= 2) {
    return { menor: Math.min(partes[0], partes[1]), maior: Math.max(partes[0], partes[1]) };
  }
  if (partes.length === 1 && partes[0] > 0) return { menor: partes[0], maior: partes[0] };
  return null;
}

function parseMaterial(cel: string): 'mdf' | 'aglomerado' {
  const n = cel.toLowerCase();
  if (n.includes('mdf')) return 'mdf';
  return 'aglomerado';
}

function parseEspessura(cel: string): number {
  const m = String(cel).match(/(\d{1,2})/);
  return m ? parseInt(m[1], 10) : 15;
}

function splitLinha(linha: string): string[] {
  if (linha.includes('\t')) return linha.split('\t').map((c) => c.trim());
  if (linha.includes(';')) return linha.split(';').map((c) => c.trim());
  return linha.split(/\s{2,}/).map((c) => c.trim());
}

/** Detecta se a primeira linha parece cabeçalho. */
function linhaEhCabecalho(cols: string[]): boolean {
  const tipos = cols.map(detectarTipoColuna);
  const uteis = tipos.filter((t) => t !== 'ignorar').length;
  return uteis >= 2 && cols.some((c) => /[a-zA-Z\u00C0-\u024F]{2,}/.test(c) && !/^\d+([.,]\d+)?$/.test(c.trim()));
}

export function parsearTextoColadoExcel(
  texto: string,
  mapaManual?: TipoColunaExcel[]
): ResultadoParseExcel {
  const avisos: string[] = [];
  const linhasRaw = String(texto || '')
    .trim()
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (linhasRaw.length === 0) {
    return { linhas: [], cabecalhosBrutos: [], mapaColunas: [], avisos: ['Nenhuma linha encontrada.'] };
  }

  const primeiraCols = splitLinha(linhasRaw[0]);
  const temCabecalho = linhaEhCabecalho(primeiraCols);
  const cabecalhosBrutos = temCabecalho ? primeiraCols : [];
  const mapaColunas: TipoColunaExcel[] =
    mapaManual && mapaManual.length > 0
      ? mapaManual
      : temCabecalho
        ? primeiraCols.map(detectarTipoColuna)
        : inferirMapaSemCabecalho(primeiraCols);

  const dadosInicio = temCabecalho ? 1 : 0;
  const linhas: LinhaParseadaExcel[] = [];

  for (let i = dadosInicio; i < linhasRaw.length; i++) {
    const cols = splitLinha(linhasRaw[i]);
    if (cols.every((c) => !c)) continue;

    let nomePeca = '';
    let quantidade = 1;
    let medidaMenor = 0;
    let medidaMaior = 0;
    let material: 'mdf' | 'aglomerado' = 'aglomerado';
    let espessura = 15;
    let largura = 0;
    let comprimento = 0;

    cols.forEach((cel, idx) => {
      const tipo = mapaColunas[idx] || 'ignorar';
      switch (tipo) {
        case 'peca':
          nomePeca = cel;
          break;
        case 'qtd':
          quantidade = Math.max(1, Math.floor(parseNum(cel)) || 1);
          break;
        case 'medida': {
          const m = parseMedidaCelula(cel);
          if (m) {
            medidaMenor = m.menor;
            medidaMaior = m.maior;
          }
          break;
        }
        case 'largura':
          largura = parseNum(cel);
          break;
        case 'comprimento':
          comprimento = parseNum(cel);
          break;
        case 'material':
          material = parseMaterial(cel);
          break;
        case 'espessura':
          espessura = parseEspessura(cel);
          break;
        default:
          break;
      }
    });

    if (largura > 0 && comprimento > 0) {
      medidaMenor = Math.min(largura, comprimento);
      medidaMaior = Math.max(largura, comprimento);
    }

    if (!nomePeca && cols[0]) nomePeca = cols[0];
    if (medidaMenor <= 0 && medidaMaior <= 0) {
      avisos.push(`Linha ${i + 1}: medida não reconhecida — ignorada.`);
      continue;
    }
    if (medidaMenor <= 0) medidaMenor = medidaMaior;
    if (medidaMaior <= 0) medidaMaior = medidaMenor;

    linhas.push({
      nomePeca: nomePeca.trim(),
      quantidade,
      medidaMenor,
      medidaMaior,
      material,
      espessura,
    });
  }

  if (linhas.length === 0 && linhasRaw.length > 0) {
    avisos.push('Nenhuma linha válida. Confira o mapeamento das colunas.');
  }

  return { linhas, cabecalhosBrutos, mapaColunas, avisos };
}

function inferirMapaSemCabecalho(primeiraLinha: string[]): TipoColunaExcel[] {
  const n = primeiraLinha.length;
  if (n >= 5) return ['peca', 'qtd', 'largura', 'comprimento', 'material', 'espessura'].slice(0, n);
  if (n === 4) return ['peca', 'qtd', 'medida', 'material'];
  if (n === 3) return ['peca', 'qtd', 'medida'];
  if (n === 2) return ['peca', 'medida'];
  return primeiraLinha.map(() => 'ignorar' as TipoColunaExcel);
}

export const ROTULOS_COLUNA_EXCEL: Record<TipoColunaExcel, string> = {
  peca: 'Peça / Descrição',
  qtd: 'Quantidade',
  medida: 'Medida (ex: 450x1700)',
  largura: 'Largura (mm)',
  comprimento: 'Comprimento (mm)',
  material: 'Material (MDF/AGLO)',
  espessura: 'Espessura (mm)',
  ignorar: '— Ignorar —',
};
